import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import Skeleton from "react-loading-skeleton";
import RTKQErrorCard from "../../../../common/components/RTKQErrorCard";
import type {
  TransformedProduct,
  TransformedVariation,
} from "../../types/productApiTypes";
import type { SelectedAttributeSlugs } from "../../hooks/useProductVariationSelector";
import type { RTKQueryError } from "../../../../common/components/RTKQErrorCard";
import ProductGallery from "./ProductGallery";
import VariationSelector from "./VariationSelector";
// Import the refactored DetailsActions
import DetailsActions from "@/js/features/cart/components/addToCarts/DetailsActions";
import { useTranslation } from "react-i18next";

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: TransformedProduct | null | undefined;
  productLoading: boolean;
  productError: boolean;
  productErrorDetails: RTKQueryError | null;
  variationsLoading?: boolean;
  variationsError?: boolean;
  variationsErrorDetails?: RTKQueryError | null;
  selectedAttributeSlugs: SelectedAttributeSlugs;
  selectedVariation: TransformedVariation | null;
  handleAttributeSelect: (attributeName: string, valueSlug: string) => void;
}

const ProductModal: React.FC<ProductModalProps> = ({
  isOpen,
  onClose,
  product,
  productLoading,
  productError,
  productErrorDetails,
  variationsLoading,
  selectedAttributeSlugs,
  selectedVariation,
  handleAttributeSelect,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [allAttributesSelected, setAllAttributesSelected] =
    useState<boolean>(false);
  const { t, ready: translationsReady } = useTranslation();

  const variationAttributeCount = useMemo(() => {
    return (
      product?.enrichedAttributes?.filter((attr) => attr.hasVariations)
        .length || 0
    );
  }, [product?.enrichedAttributes]);

  const isCombinationAvailable = selectedVariation !== null;

  // --- Calculated States (copied from ProductDetails for consistency) ---
  const isVariable = product?.type === "variable";

  const getStockStatus = (): TransformedProduct["stockStatus"] => {
    if (isVariable && selectedVariation) return selectedVariation.stockStatus;
    return product?.stockStatus || "outofstock";
  };
  const stockStatus = getStockStatus();

  const getCurrentStockQuantity = (): number | null | undefined => {
    if (isVariable && selectedVariation) {
      return selectedVariation.stockQuantity;
    }
    return product?.stockQuantity;
  };
  const currentStockQuantity = getCurrentStockQuantity();
  // --- End Calculated States ---

  useEffect(() => {
    if (
      !product ||
      product.type !== "variable" ||
      variationAttributeCount === 0
    ) {
      setAllAttributesSelected(false);
      return;
    }
    const hasAllSelected =
      Object.keys(selectedAttributeSlugs).length === variationAttributeCount;
    setAllAttributesSelected(hasAllSelected);
  }, [product, selectedAttributeSlugs, variationAttributeCount]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const isLightboxOpen = !!document.querySelector(".yarl__container");
      if (isLightboxOpen) return;
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }
    return () => {
      document.body.style.overflow = "auto";
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const currencySymbol = window.themeVariables?.currencySymbol || "₺";

  const renderModalContent = () => {
    const isLoading =
      productLoading ||
      (product?.type === "variable" && variationsLoading) ||
      !translationsReady;

    if (isLoading) {
      return renderModalLoadingSkeleton();
    }
    if (productError) {
      return (
        <div className="modal-error">
          <RTKQErrorCard
            error={productErrorDetails}
            title={t("productModal.errorLoading")}
            compact={true}
          />
        </div>
      );
    }
    if (!product) {
      return <div className="modal-error">{t("productModal.notFound")}</div>;
    }

    const shouldShowPrice =
      product?.type !== "variable" ||
      (selectedVariation &&
        selectedVariation.formattedPrice !== undefined &&
        selectedVariation.formattedPrice !== "");

    const priceData = {
      formattedPrice:
        (isVariable && selectedVariation?.formattedPrice) ||
        product.formattedPrice,
      formattedRegularPrice:
        (isVariable && selectedVariation?.formattedRegularPrice) ||
        product.formattedRegularPrice,
      formattedSalePrice:
        (isVariable && selectedVariation?.formattedSalePrice) ||
        product.formattedSalePrice,
      onSale: (isVariable && selectedVariation?.onSale) || product.onSale,
    };

    return (
      <>
        <div className="modal-header">
          <h2 className="modal-title">{product.name}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="modal-content">
            <div className="modal-gallery">
              {product && (
                <ProductGallery
                  product={product}
                  selectedVariation={selectedVariation}
                  variationsLoading={variationsLoading}
                />
              )}
            </div>
            <div className="modal-details">
              {shouldShowPrice ? (
                <div className="modal-price">
                  {priceData.onSale && priceData.formattedSalePrice ? (
                    <>
                      <span className="regular-price">
                        {priceData.formattedRegularPrice}
                        {currencySymbol}
                      </span>
                      <span className="sale-price">
                        {priceData.formattedSalePrice}
                        {currencySymbol}
                      </span>
                    </>
                  ) : (
                    <span className="regular-price current-price">
                      {priceData.formattedPrice}
                      {currencySymbol}
                    </span>
                  )}
                </div>
              ) : (
                product.type === "variable" && (
                  <div className="modal-price-placeholder">
                    {!allAttributesSelected ? (
                      <div className="attribute-selection-message">
                        {t("productModal.selectOptionsPrice")}
                      </div>
                    ) : (
                      !isCombinationAvailable && (
                        <div className="unavailable-combination-message">
                          {t("productModal.combinationUnavailable")}
                        </div>
                      )
                    )}
                  </div>
                )
              )}

              {/* Conditionally render stock status */}
              {!(isVariable && !isCombinationAvailable) && (
                <div className="stock-status">
                  {variationsLoading ? (
                    <span className="loading-status">
                      {t("productModal.checkingAvailability")}
                    </span>
                  ) : stockStatus === "outofstock" ? (
                    <span className="out-of-stock">
                      {t("productModal.outOfStock")}
                    </span>
                  ) : stockStatus === "onbackorder" ? (
                    <span className="on-backorder">
                      {t("productModal.onBackorder")}
                    </span>
                  ) : (
                    <span className="in-stock">
                      {t("productModal.inStock")}
                    </span>
                  )}
                </div>
              )}

              {product.shortDescription && (
                <div
                  className="modal-description"
                  dangerouslySetInnerHTML={{ __html: product.shortDescription }}
                />
              )}

              {product.type === "variable" && (
                <VariationSelector
                  enrichedAttributes={product.enrichedAttributes || []}
                  selectedAttributes={selectedAttributeSlugs}
                  onAttributeSelect={handleAttributeSelect}
                  variationsLoading={variationsLoading}
                  containerClassName="modal-variations"
                  rowClassName="variation-select"
                />
              )}

              {/* REMOVE the old .modal-actions div */}
              {/* <div className="modal-actions"> ... </div> */}

              {/* ADD the new DetailsActions component here */}
              <DetailsActions
                product={product}
                selectedVariation={selectedVariation}
                isVariable={isVariable}
                isCombinationAvailable={isCombinationAvailable}
                hasRequiredAttributes={allAttributesSelected} // Use the correct flag
                variationsLoading={isLoading}
                stockStatus={stockStatus}
                stockQuantity={currentStockQuantity}
                selectedAttributes={selectedAttributeSlugs}
                showQuantitySelector={false} // Do not show quantity in modal
                showViewDetailsButton={true} // Show view details button in modal
                containerClassName="modal-details-actions" // Optional: Add specific class for modal context
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderModalLoadingSkeleton = () => (
    <>
      <div className="modal-header">
        <h2 className="modal-title">
          <Skeleton width={200} height={24} />
        </h2>
        <button className="modal-close" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="modal-body">
        <div className="modal-content">
          <div className="modal-gallery">
            <div className="product-gallery product-gallery-loading">
              <Skeleton height={400} className="gallery-skeleton" />
              <div
                className="thumbnails-skeleton"
                style={{ marginTop: "10px" }}
              >
                <Skeleton
                  height={80}
                  width={80}
                  count={4}
                  inline={true}
                  style={{ marginRight: "10px" }}
                />
              </div>
            </div>
          </div>
          <div className="modal-details">
            <div className="modal-price">
              <Skeleton width={120} height={24} />
            </div>
            <div className="stock-status">
              <Skeleton width={150} height={20} />
            </div>
            <div className="modal-description">
              <Skeleton count={2} />
            </div>
            <VariationSelector
              enrichedAttributes={[]}
              selectedAttributes={{}}
              onAttributeSelect={() => {}}
              variationsLoading={true}
              containerClassName="modal-variations"
              rowClassName="variation-select"
            />
            {/* Skeleton for the new actions area */}
            <div className="modal-details-actions skeleton-actions">
              <Skeleton height={36} width="48%" />
              <Skeleton height={36} width="48%" />
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(
    <div className="product-modal-overlay">
      <div className="product-modal" ref={modalRef}>
        {renderModalContent()}
      </div>
    </div>,
    document.body
  );
};

export default ProductModal;
